import React, { memo, useCallback } from 'react';
import { View, FlatList, Text, ActivityIndicator, StyleSheet } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { ReportReviewListSkeleton } from './ReportReviewListSkeleton';
import { ReportReviewListItem } from './ReportReviewListItem';
import type { CorkBoardReport } from '@/types/residents/corkBoard';

interface ReportReviewListProps {
  reports: CorkBoardReport[];
  isLoading: boolean;
  isMutating?: boolean;
  onView: (report: CorkBoardReport) => void;
  onRemove: (report: CorkBoardReport) => void;
  onDismiss: (report: CorkBoardReport) => void;
  onWarn: (report: CorkBoardReport) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
  emptyMessage?: string;
}

export const ReportReviewList: React.FC<ReportReviewListProps> = memo(
  ({
    reports,
    isLoading,
    isMutating,
    onView,
    onRemove,
    onDismiss,
    onWarn,
    onRefresh,
    isRefreshing,
    onLoadMore,
    hasNextPage,
    isFetchingMore,
    emptyMessage,
  }) => {
    const { redesign } = useAdminColors();

    const renderItem = useCallback(
      ({ item }: { item: CorkBoardReport }) => (
        <ReportReviewListItem
          report={item}
          isMutating={isMutating ?? false}
          onView={onView}
          onRemove={onRemove}
          onDismiss={onDismiss}
          onWarn={onWarn}
        />
      ),
      [onView, onRemove, onDismiss, onWarn, isMutating],
    );

    const keyExtractor = useCallback((item: CorkBoardReport) => item.id, []);

    const handleEndReached = useCallback(() => {
      if (hasNextPage && !isFetchingMore && onLoadMore) {
        onLoadMore();
      }
    }, [hasNextPage, isFetchingMore, onLoadMore]);

    const renderFooter = useCallback(() => {
      if (!isFetchingMore) return null;
      return (
        <View style={styles.footer}>
          <ActivityIndicator size="small" color={redesign.accent.blueBg} />
        </View>
      );
    }, [isFetchingMore, redesign.accent.blueBg]);

    if (isLoading && reports.length === 0) {
      return <ReportReviewListSkeleton />;
    }

    if (!isLoading && reports.length === 0) {
      return (
        <View style={styles.centered}>
          <SVG_COMPONENTS.EMPTY_MESSAGE width={48} height={48} color={redesign.text.subtle} />
          <Text style={[styles.emptyTitle, { color: redesign.text.primary }]}>No reports</Text>
          <Text style={[styles.emptySubtitle, { color: redesign.text.secondary }]}>
            {emptyMessage || 'No reports match the current filter.'}
          </Text>
        </View>
      );
    }

    return (
      <FlatList
        data={reports}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        onRefresh={onRefresh}
        refreshing={isRefreshing ?? false}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.4}
        ListFooterComponent={renderFooter}
      />
    );
  },
);

ReportReviewList.displayName = 'ReportReviewList';

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  listContent: {
    flexGrow: 1,
    paddingTop: 8,
    paddingBottom: 8,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    minHeight: 200,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    textAlign: 'center',
    maxWidth: 300,
  },
  footer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
});
